import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermissoes } from '../../context/PermissoesContext';   // r165
import tarefaService from '../../services/tarefa.service';
import apontamentoService from '../../services/apontamento.service';
import usuarioService from '../../services/usuario.service';
import tarefaAnexoService from '../../services/tarefaAnexo.service';   // r199
import SePode from '../../components/SePode';   // r90: prote\u00e7\u00e3o de bot\u00f5es no modal
import ApontamentoModal from './ApontamentoModal';
import CalendarioSelecaoOverlay from './CalendarioSelecaoOverlay';
import ChatComentarios from './ChatComentarios';
import { AnexosCabecalho, TarefaAnexosModal } from './TarefaAnexos';   // r199: anexos da tarefa
import { iconeDaRotina } from '../../constants/iconesRotinas';   // r172: ícone SVG da rotina no header

// ════════════════════════════════════════════════════════════════════════════
// TarefaModal — versão reformulada (r26)
// ────────────────────────────────────────────────────────────────────────────
// Mudanças em relação à versão anterior:
//   • Tela cheia (ocupa toda a área de conteúdo do portal)
//   • Seções colapsáveis (não abas) — padrão NegociosPage <Secao>
//   • Fonte Manrope (inputs) + Calibri (títulos de seção)
//   • Tarefa-mãe NÃO é informada manualmente — só vem preenchida quando
//     o modal é aberto via "+ Nova sub-tarefa" na seção de vínculos
//   • Lista de vínculos hierárquicos (mãe + filhas no mesmo espaço, com
//     flag [MÃE]/[FILHA] visual)
//   • Negócio sem cliente: ao selecionar o negócio, cliente é preenchido
//     automaticamente (vem do negócio)
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#262626', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  verde:   '#10b981', verdeBg: '#f0fff4',
  // r178: verde exclusivo do status 'Em Andamento' (nao reaproveitar noutros
  //       elementos -- COR.verde segue valendo pro resto).
  verdeAndamento: '#009900',
  cinzaNaoIniciado: '#CCCCCC',
  vermelho:'#dc2626', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  // r182: amarelo do status 'Pendente de Retorno' (o COR.amarelo do Pausado
  //       e mais ambar -- os dois precisam ser distinguiveis lado a lado).
  amareloRetorno: '#eab308', amlRetBg: '#fefce8',
  roxo:    '#7c3aed',
  // r182: contorno e label dos campos num cinza mais escuro. COR.borda (claro)
  //       segue valendo pros divisores, popovers e cartões.
  bordaCampo: '#9ca3af', labelCampo: '#4b5563',
};

// Estilos base — Manrope Semibold nos inputs, Calibri nos títulos de seção
const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.bordaCampo}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  fontFamily: "'Manrope', sans-serif", fontWeight: '600',
};

// Texto dos combos inline (Cliente/Projeto) no mesmo padrão dos inputs
const estiloTextoCombo = { color: COR.texto, fontWeight: '600' };

const selectStyle = { ...inputStyle, cursor: 'pointer' };

const textareaStyle = {
  ...inputStyle, height: 'auto', minHeight: '80px', padding: '8px 10px',
  resize: 'vertical',
};

// r172: botões do header (sobre fundo azul, padrão Modal rico)
const btnHeaderClaro = {
  padding: '7px 14px', fontSize: '12px', fontWeight: '600',
  border: '1px solid rgba(255,255,255,0.5)', borderRadius: '5px',
  backgroundColor: 'transparent', color: '#fff', cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif", whiteSpace: 'nowrap',
};
const btnHeaderSalvar = {
  padding: '7px 18px', fontSize: '12px', fontWeight: '700',
  border: '1px solid #fff', borderRadius: '5px',
  backgroundColor: '#fff', color: '#003399', cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif", whiteSpace: 'nowrap',
};
const btnHeaderPerigo = {
  padding: '7px 14px', fontSize: '12px', fontWeight: '600',
  border: '1px solid rgba(255,255,255,0.5)', borderRadius: '5px',
  backgroundColor: 'transparent', color: '#ffd7d7', cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif", whiteSpace: 'nowrap',
};

const FORM_VAZIO = {
  titulo: '',
  descricao: '',
  chamado: '',              // r215: nº do chamado no sistema do cliente
  setor: '',
  status_execucao: 'N',
  prioridade: 'B',
  responsavel_id: '',
  deadline: '',
  cliente_id: '',
  cliente_nome: '',
  cliente_tag: '',
  cliente_cor_tag: '',
  negocio_id: '',
  negocio_label: '',
  // tarefa_mae_id NÃO está mais no form — vem só via prop tarefaMae
};

const SETORES = [
  { v: 'C', label: 'Comercial',     cor: '#2563eb' },
  { v: 'T', label: 'Técnico',        cor: '#7c3aed' },
  { v: 'Q', label: 'Qualidade',      cor: '#f97316' },
  { v: 'A', label: 'Administrativo', cor: '#6b7280' },
  { v: 'P', label: 'Apoio Técnico',  cor: '#db2777' },
  { v: 'O', label: 'Outros',         cor: '#06b6d4' },
];

const STATUS_LABEL = {
  N: 'Não Iniciado', E: 'Em Andamento', X: 'Pausado',
  R: 'Pendente de Retorno',                       // r182
  P: 'Pendente Filho', F: 'Finalizado', S: 'Sandbox',
};
const STATUS_COR = {
  N: COR.muted, E: COR.verde, X: COR.amarelo, R: COR.amareloRetorno,
  P: '#3b82f6', F: COR.vermelho, S: '#f97316',
};

// Status que aparecem no combo do header (Sandbox fica oculto, é só técnico)
const STATUS_OPCOES = [
  { v: 'N', label: 'Não Iniciado',   cor: COR.cinzaNaoIniciado    },
  { v: 'E', label: 'Em Andamento',   cor: COR.verdeAndamento    },
  { v: 'X', label: 'Pausado',        cor: COR.amarelo  },
  { v: 'R', label: 'Pendente de Retorno', cor: COR.amareloRetorno },
  { v: 'F', label: 'Finalizado',     cor: COR.vermelho },
  { v: 'P', label: 'Pendente Filho', cor: '#3b82f6'    },
];

const PRIORIDADE_OPCOES = [
  { v: 'A', label: 'Alta',  cor: COR.vermelho, icone: '⬆' },
  { v: 'M', label: 'Média', cor: COR.amarelo,  icone: '➤' },
  { v: 'B', label: 'Baixa', cor: COR.verde,    icone: '⬇' },
];

// ─── Helpers de formato ─────────────────────────────────────────────────────
function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

function toDateLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
}

// ════════════════════════════════════════════════════════════════════════════
// <Campo> — wrapper de label + control (idêntico ao padrão do portal)
// ════════════════════════════════════════════════════════════════════════════
function Campo({ label, obrigatorio, metade, terco, quarto, auto, children }) {
  let width = '100%';
  if (metade) width = 'calc(50% - 6px)';
  if (terco)  width = 'calc(33.333% - 8px)';
  if (quarto) width = 'calc(25% - 9px)';
  if (auto)   width = 'auto';
  return (
    <div style={{ marginBottom: '12px', width }}>
      <label style={{
        display: 'block', fontSize: '12px', fontWeight: '600',
        color: COR.labelCampo, marginBottom: '4px',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {label}{obrigatorio && <span style={{ color: COR.vermelho }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <Secao> — seção colapsável (mesmo padrão visual de NegociosPage)
// ════════════════════════════════════════════════════════════════════════════
// ─── r172: pílulas no padrão do browse de Lançamento de Horas ───────────────
// Normaliza hex de 3 dígitos (#888) → 6 (#888888) pro truque cor+'22' (fundo claro).
function corHex6(c) {
  if (typeof c === 'string' && /^#[0-9a-fA-F]{3}$/.test(c)) {
    return '#' + c.slice(1).split('').map(x => x + x).join('');
  }
  return c;
}
function Pill({ label, cor, solido, icone }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '2px 10px', borderRadius: '20px',
    fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
    lineHeight: 1.5, border: `1px solid ${cor}`,
  };
  const estilo = solido
    ? { ...base, backgroundColor: cor, color: COR.branco, letterSpacing: '0.3px' }
    : { ...base, backgroundColor: corHex6(cor) + '22', color: cor };
  return <span style={estilo}>{icone && <span>{icone}</span>}{label}</span>;
}

// ─── r172: combo compacto — mostra a pílula do valor e abre dropdown de opções ─
function PillCombo({ valor, opcoes, onChange, disabled, placeholder = 'Selecione' }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!aberto) return;
    const fechar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [aberto]);
  const sel = opcoes.find(o => o.v === valor);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" disabled={disabled}
        onClick={() => !disabled && setAberto(a => !a)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: COR.branco, border: `1px solid ${COR.borda}`,
          borderRadius: '20px', padding: '3px 8px 3px 4px',
          minHeight: '30px', boxSizing: 'border-box',   // r172d: altura fixa mesmo vazio
          cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
        }}>
        {sel
          ? <Pill label={sel.label} cor={sel.cor} solido={sel.solido} icone={sel.icone} />
          : <span style={{ fontSize: '12px', color: COR.muted, padding: '0 4px' }}>{placeholder}</span>}
        {!disabled && <span style={{ fontSize: '10px', color: COR.muted }}>▾</span>}
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 60,
          background: COR.branco, border: `1px solid ${COR.borda}`, borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '6px',
          display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '170px',
        }}>
          {opcoes.map(o => (
            <button key={o.v} type="button"
              onClick={() => { onChange(o.v); setAberto(false); }}
              style={{
                display: 'flex', alignItems: 'center',
                background: o.v === valor ? COR.azulBg : 'transparent',
                border: 'none', borderRadius: '6px', padding: '5px 8px',
                cursor: 'pointer', textAlign: 'left',
              }}>
              <Pill label={o.label} cor={o.cor} solido={o.solido} icone={o.icone} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── r172: chevron giratório e ícones SVG das seções (padrão ClientesPage) ──
const SvgChevron = ({ size = 16, color = COR.muted, aberto }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
       style={{ transform: aberto ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const SvgIdentificacao = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2h6a1 1 0 0 1 1 1v1h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2V3a1 1 0 0 1 1-1z" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);
const SvgApontamentos = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
  </svg>
);
const SvgComentarios = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const SvgHistorico = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 8v4l3 2" />
  </svg>
);
// r215: avatar do citado — foto quando houver, senão as iniciais do nome.
//       Mesma construção de URL usada no rodapé do Sidebar (host do navegador).
function AvatarCitado({ nome, foto }) {
  const iniciais = (nome || '?')
    .split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  const url = (() => {
    if (!foto) return '';
    if (foto.startsWith('http')) return foto;
    const base = process.env.REACT_APP_API_BASE
      ? process.env.REACT_APP_API_BASE.replace(/\/api$/, '')
      : '';
    return `${base}${foto}`;
  })();
  return (
    <div style={{
      width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
      backgroundColor: COR.azulBg, color: COR.azul,
      backgroundImage: url ? `url(${url})` : 'none',
      backgroundSize: 'cover', backgroundPosition: 'center',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', fontWeight: '700',
    }}>
      {!url && iniciais}
    </div>
  );
}

// r215: ícone da seção "Colaboradores Citados" — duas pessoas com um "@"
const SvgCitados = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-6 6-6 1 0 2 .25 2.8.7" />
    <circle cx="17.5" cy="16.5" r="3.2" />
    <path d="M20.7 16.5v1.2a1.6 1.6 0 01-3.2 0v-1.2" />
  </svg>
);

const SvgVinculos = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
    <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
  </svg>
);

// ─── r172: seção expansível no padrão do cadastro de Cliente ────────────────
function Secao({ icone: Icone, titulo, aberta, onToggle, acao, children }) {
  return (
    <div style={{
      border: `1px solid ${COR.borda}`, borderRadius: '6px',
      marginBottom: '12px', overflow: 'visible', backgroundColor: COR.branco,
    }}>
      <div onClick={onToggle} style={{
        padding: '12px 16px',
        backgroundColor: aberta ? COR.azulBg : COR.cinzaBg,
        borderBottom: aberta ? `1px solid ${COR.borda}` : 'none',
        cursor: 'pointer', userSelect: 'none',
        display: 'flex', alignItems: 'center', gap: '10px',
        transition: 'background-color 0.15s',
      }}>
        {Icone && (
          <span style={{ display: 'inline-flex', color: aberta ? COR.azul : COR.muted }}>
            <Icone size={18} />
          </span>
        )}
        <span style={{
          flex: 1, fontSize: '14px', fontWeight: '700',
          color: aberta ? COR.azul : COR.texto,
        }}>
          {titulo}
        </span>
        {/* Ação inline (ex: botão "+ Nova sub-tarefa") — só visível quando aberta */}
        {aberta && acao && (
          <div onClick={e => e.stopPropagation()}>{acao}</div>
        )}
        <span style={{ display: 'inline-flex', color: COR.muted }}>
          <SvgChevron aberto={aberta} size={16} />
        </span>
      </div>
      {aberta && <div style={{ padding: '16px' }}>{children}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <TagCliente> — réplica do componente de ClientesPage (luminância + cor texto)
// ════════════════════════════════════════════════════════════════════════════
function TagCliente({ tag, cor, titulo }) {
  if (!tag) return null;
  const bgCor = cor || '#003399';
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0,2),16) || 0;
  const g = parseInt(hex.substr(2,2),16) || 0;
  const b = parseInt(hex.substr(4,2),16) || 0;
  const lum = (0.299*r + 0.587*g + 0.114*b) / 255;
  const corTexto = lum > 0.5 ? '#1a1a2e' : '#ffffff';
  return (
    <span title={titulo} style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '11px', fontWeight: '500',
      backgroundColor: bgCor, color: corTexto,
      letterSpacing: '0.3px',
      fontFamily: "'Manrope', sans-serif",
    }}>{tag}</span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <ClienteSelector> — botão "Selecionar cliente" ou tag + nome + ×
// ════════════════════════════════════════════════════════════════════════════
function ClienteSelector({ cliente, onSelecionar, desabilitado }) {
  const [aberto, setAberto] = useState(false);

  if (!cliente?.id) {
    return (
      <>
        <button
          type="button"
          onClick={() => setAberto(true)}
          disabled={desabilitado}
          style={{
            ...inputStyle, cursor: desabilitado ? 'default' : 'pointer',
            color: COR.muted, textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: '8px',
            backgroundColor: desabilitado ? COR.cinzaBg : COR.branco,
          }}
        >
          <span style={{ fontSize: '14px' }}>👤</span>
          <span>Selecionar cliente...</span>
        </button>
        {aberto && (
          <ClienteListaModal
            onEscolher={(c) => { onSelecionar(c); setAberto(false); }}
            onFechar={() => setAberto(false)}
          />
        )}
      </>
    );
  }

  return (
    <div style={{
      ...inputStyle, height: 'auto', minHeight: '36px', padding: '6px 10px',
      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    }}>
      <TagCliente tag={cliente.tag} cor={cliente.cor_tag}
                  titulo={cliente.nome_fantasia || cliente.razao_social} />
      <span style={{ fontSize: '13px', color: COR.texto, flex: 1 }}>
        {cliente.nome_fantasia || cliente.razao_social}
      </span>
      {!desabilitado && (
        <button
          type="button"
          onClick={() => onSelecionar(null)}
          title="Remover cliente"
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            color: COR.muted, fontSize: '16px', padding: '0 4px',
          }}
        >×</button>
      )}
    </div>
  );
}

// ─── Modal interno de seleção de cliente ────────────────────────────────────
function ClienteListaModal({ onEscolher, onFechar }) {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = termo.trim();
      if (q.length < 1) { setResultados([]); return; }
      try {
        setCarregando(true);
        const dados = await tarefaService.buscarClientes(q);
        setResultados(dados);
      } catch (err) {
        console.warn('Erro buscar clientes:', err);
        setResultados([]);
      } finally {
        setCarregando(false);
      }
    }, 250);
  }, [termo]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onFechar]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      paddingTop: '15vh',
      fontFamily: "'Manrope', sans-serif",
    }} onClick={onFechar}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        width: '480px', maxWidth: '90vw', maxHeight: '60vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 16px', borderBottom: `1px solid ${COR.borda}`,
          fontSize: '13px', fontWeight: '600', color: COR.texto,
          fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          Selecionar cliente
        </div>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COR.borda}` }}>
          <input
            type="text" autoFocus
            value={termo} onChange={(e) => setTermo(e.target.value)}
            placeholder="Nome fantasia, razão social ou CNPJ..."
            style={inputStyle}
          />
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {carregando ? (
            <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: COR.muted }}>
              Buscando...
            </div>
          ) : resultados.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: COR.muted }}>
              {termo.trim() ? 'Nenhum cliente encontrado.' : 'Comece digitando.'}
            </div>
          ) : resultados.map((c, i) => (
            <div key={c.id} onClick={() => onEscolher(c)}
                 style={{
                   padding: '10px 16px', cursor: 'pointer',
                   borderBottom: i < resultados.length - 1 ? `1px solid ${COR.borda}` : 'none',
                   display: 'flex', alignItems: 'center', gap: '10px',
                 }}
                 onMouseEnter={e => e.currentTarget.style.backgroundColor = COR.cinzaBg}
                 onMouseLeave={e => e.currentTarget.style.backgroundColor = COR.branco}
            >
              <TagCliente tag={c.tag} cor={c.cor_tag}
                          titulo={c.nome_fantasia || c.razao_social} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: COR.texto }}>
                  {c.nome_fantasia || c.razao_social}
                </div>
                {c.nome_fantasia && (
                  <div style={{ fontSize: '10px', color: COR.muted }}>
                    {c.razao_social}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <PrioridadeMenu> — texto colorido + menu flutuante
// ════════════════════════════════════════════════════════════════════════════
function PrioridadeMenu({ valor, onSelecionar, desabilitado }) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const atual = PRIORIDADE_OPCOES.find(o => o.v === valor) || PRIORIDADE_OPCOES[2];

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => !desabilitado && setAberto(o => !o)}
        disabled={desabilitado}
        title="Clique para alterar a prioridade"
        style={{
          // Tag/pill: arredondada, com altura próxima ao input
          height: '36px', padding: '0 18px',
          border: `1px solid ${atual.cor}66`,
          borderRadius: '20px',     // pill
          backgroundColor: atual.cor + '15',
          color: atual.cor,
          fontSize: '13px', fontWeight: '700',
          cursor: desabilitado ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', gap: '8px',
          whiteSpace: 'nowrap',
          fontFamily: "'Manrope', sans-serif",
          letterSpacing: '0.3px',
        }}
      >
        <span style={{ fontSize: '15px' }}>{atual.icone}</span>
        <span>{atual.label.toUpperCase()}</span>
        {!desabilitado && <span style={{ fontSize: '10px', opacity: 0.7 }}>▾</span>}
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100,
          backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: '120px', overflow: 'hidden',
        }}>
          {PRIORIDADE_OPCOES.map(o => (
            <div key={o.v}
                 onClick={() => { onSelecionar(o.v); setAberto(false); }}
                 style={{
                   padding: '8px 12px', cursor: 'pointer',
                   display: 'flex', alignItems: 'center', gap: '8px',
                   color: o.cor, fontWeight: '700', fontSize: '12px',
                   backgroundColor: valor === o.v ? o.cor + '10' : COR.branco,
                   fontFamily: "'Manrope', sans-serif",
                 }}
                 onMouseEnter={e => e.currentTarget.style.backgroundColor = o.cor + '20'}
                 onMouseLeave={e => e.currentTarget.style.backgroundColor =
                   valor === o.v ? o.cor + '10' : COR.branco}
            >
              <span style={{ fontSize: '13px' }}>{o.icone}</span>
              <span>{o.label.toUpperCase()}</span>
              {valor === o.v && <span style={{ marginLeft: 'auto' }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <Autocomplete> — usado pra negócio (busca com debounce 250ms)
// ════════════════════════════════════════════════════════════════════════════
function Autocomplete({ valor, label, onSelecionar, buscar, formatItem, placeholder, desabilitado }) {
  const [termo, setTermo] = useState(label || '');
  const [resultados, setResultados] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setTermo(label || ''); }, [label, valor]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setTermo(v);
    setAberto(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!v.trim()) {
      onSelecionar({ id: '', label: '', raw: null });
      setResultados([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setCarregando(true);
        const dados = await buscar(v);
        setResultados(dados.map(item => ({ ...formatItem(item), raw: item })));
      } catch (err) {
        console.warn('Erro autocomplete:', err);
        setResultados([]);
      } finally {
        setCarregando(false);
      }
    }, 250);
  };

  const escolher = (item) => {
    onSelecionar(item);
    setTermo(item.label);
    setAberto(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text" value={termo} onChange={handleChange}
        onFocus={() => termo && setAberto(true)}
        disabled={desabilitado}
        placeholder={placeholder || 'Digite para buscar...'}
        style={inputStyle}
      />
      {aberto && (resultados.length > 0 || carregando) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
          marginTop: '2px', backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxHeight: '220px', overflowY: 'auto',
        }}>
          {carregando ? (
            <div style={{ padding: '8px 12px', fontSize: '11px', color: COR.muted }}>
              Buscando...
            </div>
          ) : resultados.map((item, i) => (
            <div
              key={item.id + '_' + i}
              onClick={() => escolher(item)}
              style={{
                padding: '8px 12px', fontSize: '12px',
                cursor: 'pointer', color: COR.texto,
                borderBottom: i < resultados.length - 1 ? `1px solid ${COR.borda}` : 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = COR.cinzaBg}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = COR.branco}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <CardVinculo> — card de tarefa na lista lateral de "Tarefas Vinculadas"
// flag pode ser 'MÃE' (roxo) ou 'FILHA' (verde)
// ════════════════════════════════════════════════════════════════════════════
function CardVinculo({ tarefa, flag, flagCor, onAbrir }) {
  const corStatus = STATUS_COR[tarefa.status_execucao] || COR.muted;
  const statusLabel = STATUS_LABEL[tarefa.status_execucao] || tarefa.status_execucao;

  return (
    <div
      onClick={() => onAbrir && onAbrir(tarefa)}
      style={{
        border: `1px solid ${COR.borda}`,
        borderLeft: `4px solid ${corStatus}`,
        borderRadius: '5px',
        padding: '10px 12px',
        fontSize: '12px',
        backgroundColor: COR.branco,
        cursor: onAbrir ? 'pointer' : 'default',
        fontFamily: "'Manrope', sans-serif",
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => { if (onAbrir) e.currentTarget.style.boxShadow = `0 2px 8px ${COR.borda}`; }}
      onMouseLeave={e => { if (onAbrir) e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Linha 1: flag + ID */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
          fontSize: '9px', fontWeight: '700', letterSpacing: '0.5px',
          backgroundColor: flagCor + '20', color: flagCor,
          border: `1px solid ${flagCor}66`,
        }}>{flag}</span>
        <span style={{ color: COR.muted, fontSize: '11px' }}>
          #{tarefa.id}
        </span>
        <span style={{
          marginLeft: 'auto',
          fontSize: '9px', color: corStatus, fontWeight: '700',
          padding: '2px 6px', borderRadius: '8px',
          backgroundColor: corStatus + '15',
          border: `1px solid ${corStatus}33`,
        }}>
          {statusLabel.toUpperCase()}
        </span>
      </div>
      {/* Linha 2: título */}
      <div style={{
        color: COR.texto, fontWeight: '600', fontSize: '13px',
        lineHeight: '1.3', marginBottom: '6px',
      }}>
        {tarefa.titulo}
      </div>
      {/* Linha 3: responsável + deadline (se existirem) */}
      {(tarefa.responsavel_nome || tarefa.deadline) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          fontSize: '10px', color: COR.muted,
        }}>
          {tarefa.responsavel_nome && (
            <span>👤 {tarefa.responsavel_nome}</span>
          )}
          {tarefa.deadline && (
            <span>🚩 {new Date(tarefa.deadline).toLocaleDateString('pt-BR')}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <ComboboxInline> — combo editable estilo browser nativo, mas custom:
//   • Digitação no input filtra E mostra dropdown
//   • Clique no campo (mesmo vazio) abre a lista completa
//   • Setas ↑↓ navegam, Enter seleciona, Esc fecha
//   • Quando preenchido, mostra o conteúdo renderizado por renderSelecionado
//     (ex: tag colorida do cliente) com botão × pra limpar
// ────────────────────────────────────────────────────────────────────────────
// Props:
//   - valor: id selecionado
//   - labelAtual: texto a mostrar no input quando há seleção
//   - renderSelecionado: (raw)=>JSX (opcional) — pra exibir tag em vez de texto
//   - rawAtual: objeto completo do item selecionado (pra renderSelecionado)
//   - buscar: (termo)=>Promise<array>  — chamado ao focar/digitar (debounce 200ms)
//   - renderItem: (raw)=>JSX  — opcional, customiza a linha do dropdown
//   - getId, getLabel: extratores
//   - onSelecionar: ({id, label, raw}|null)  null = limpou
//   - placeholder, desabilitado
// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
// <StatusMenu> — botão pill colorido + menu flutuante com 5 opções
// (mesmo padrão do PrioridadeMenu, mas pra status_execucao da tarefa)
// ════════════════════════════════════════════════════════════════════════════
function StatusMenu({ valor, onSelecionar, desabilitado }) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const atual = STATUS_OPCOES.find(o => o.v === valor)
             || { v: valor, label: STATUS_LABEL[valor] || valor, cor: STATUS_COR[valor] || COR.muted };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => !desabilitado && setAberto(o => !o)}
        disabled={desabilitado}
        title="Clique para alterar o status"
        style={{
          height: '34px', padding: '0 16px',
          border: `1px solid ${atual.cor}66`,
          borderRadius: '17px',
          backgroundColor: atual.cor + '15',
          color: atual.cor,
          fontSize: '12px', fontWeight: '700',
          cursor: desabilitado ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', gap: '8px',
          whiteSpace: 'nowrap',
          fontFamily: "'Manrope', sans-serif",
          letterSpacing: '0.3px',
        }}
      >
        <span style={{
          width: '6px', height: '6px', borderRadius: '50%',
          backgroundColor: atual.cor, display: 'inline-block',
        }} />
        <span>{atual.label.toUpperCase()}</span>
        {!desabilitado && <span style={{ fontSize: '10px', opacity: 0.7 }}>▾</span>}
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100,
          backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: '180px', overflow: 'hidden',
        }}>
          {STATUS_OPCOES.map(o => (
            <div key={o.v}
                 onClick={() => { onSelecionar(o.v); setAberto(false); }}
                 style={{
                   padding: '9px 14px', cursor: 'pointer',
                   display: 'flex', alignItems: 'center', gap: '10px',
                   color: o.cor, fontWeight: '700', fontSize: '12px',
                   backgroundColor: valor === o.v ? o.cor + '10' : COR.branco,
                   fontFamily: "'Manrope', sans-serif",
                 }}
                 onMouseEnter={e => e.currentTarget.style.backgroundColor = o.cor + '20'}
                 onMouseLeave={e => e.currentTarget.style.backgroundColor =
                   valor === o.v ? o.cor + '10' : COR.branco}
            >
              <span style={{
                width: '10px', height: '10px', borderRadius: '50%',
                backgroundColor: o.cor, display: 'inline-block',
              }} />
              <span>{o.label.toUpperCase()}</span>
              {valor === o.v && <span style={{ marginLeft: 'auto' }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <ComboboxInline> — combo editable estilo browser nativo, mas custom:
function ComboboxInline({
  valor, labelAtual, rawAtual,
  buscar, renderItem, renderSelecionado,
  getId, getLabel,
  onSelecionar, placeholder, desabilitado,
}) {
  const [termoBusca, setTermoBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [destacado, setDestacado] = useState(-1);
  const [editando, setEditando] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const carregarLista = useCallback((termo) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setCarregando(true);
        const dados = await buscar(termo);
        setResultados(dados);
        setDestacado(dados.length > 0 ? 0 : -1);
      } catch (err) {
        console.warn('Erro combobox:', err);
        setResultados([]);
      } finally {
        setCarregando(false);
      }
    }, 200);
  }, [buscar]);

  useEffect(() => {
    if (aberto) carregarLista(termoBusca);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
        setEditando(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setTermoBusca(v);
    setAberto(true);
    carregarLista(v);
  };

  const handleFocus = () => {
    setEditando(true);
    setTermoBusca('');
    setAberto(true);
  };

  const escolher = (raw) => {
    onSelecionar({ id: getId(raw), label: getLabel(raw), raw });
    setAberto(false);
    setEditando(false);
    setTermoBusca('');
  };

  const limpar = () => {
    onSelecionar(null);
    setTermoBusca('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!aberto) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDestacado(d => Math.min(d + 1, resultados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestacado(d => Math.max(d - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (destacado >= 0 && destacado < resultados.length) {
        escolher(resultados[destacado]);
      }
    } else if (e.key === 'Escape') {
      setAberto(false);
      setEditando(false);
    }
  };

  const temSelecao = !!valor;
  const mostrarInput = !temSelecao || editando;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {mostrarInput ? (
        <input
          ref={inputRef}
          type="text"
          value={editando ? termoBusca : ''}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={desabilitado}
          placeholder={placeholder || 'Clique ou digite pra buscar...'}
          style={inputStyle}
          autoComplete="off"
        />
      ) : (
        <div
          onClick={() => {
            if (desabilitado) return;
            setEditando(true);
            setAberto(true);
            setTermoBusca('');
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          style={{
            ...inputStyle, height: 'auto', minHeight: '36px', padding: '6px 10px',
            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
            cursor: desabilitado ? 'default' : 'text',
          }}
        >
          {renderSelecionado ? (
            renderSelecionado(rawAtual)
          ) : (
            <span style={{ fontSize: '13px', color: COR.texto, flex: 1 }}>
              {labelAtual}
            </span>
          )}
          {!desabilitado && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); limpar(); }}
              title="Remover"
              style={{
                marginLeft: 'auto',
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: COR.muted, fontSize: '16px', padding: '0 4px',
              }}
            >×</button>
          )}
        </div>
      )}

      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          marginTop: '2px', backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxHeight: '280px', overflowY: 'auto',
        }}>
          {carregando ? (
            <div style={{ padding: '10px 14px', fontSize: '11px', color: COR.muted }}>
              Buscando...
            </div>
          ) : resultados.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: '11px', color: COR.muted }}>
              Nenhum resultado.
            </div>
          ) : resultados.map((raw, i) => (
            <div
              key={getId(raw) + '_' + i}
              onMouseEnter={() => setDestacado(i)}
              onClick={() => escolher(raw)}
              style={{
                padding: '8px 12px', fontSize: '12px',
                cursor: 'pointer',
                backgroundColor: i === destacado ? COR.cinzaBg : COR.branco,
                borderBottom: i < resultados.length - 1 ? `1px solid ${COR.borda}` : 'none',
              }}
            >
              {renderItem ? renderItem(raw) : (
                <span style={{ color: COR.texto }}>{getLabel(raw)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <DescricaoChecklist> — textarea de descrição com toggle Texto livre / Lista
// ────────────────────────────────────────────────────────────────────────────
// Em modo TEXTO: textarea normal
// Em modo LISTA: cada linha começada com `- [ ]` ou `- [x]` vira checkbox
//                interativo. Marcando/desmarcando atualiza o texto subjacente.
//                Demais linhas viram texto normal acima.
//
// Botão "+ Adicionar item" insere uma linha `- [ ] ` no fim.
// Toggle no canto superior direito alterna entre os 2 modos.
//
// Valor armazenado é SEMPRE texto puro (markdown) — preserva compatibilidade
// com o banco de dados. Conversão é só visual.
// ════════════════════════════════════════════════════════════════════════════
// r199b: `rodapeExtra` entra na barra de baixo, ao lado do Expandir — é
//        onde vivem os anexos da tarefa (antes ficavam no header azul).
function DescricaoChecklist({ valor, onChange, disabled, placeholder, rodapeExtra }) {
  // Detecta automaticamente se já tem checklist no texto
  const temCheckBox = /^- \[[ x]\] /m.test(valor || '');
  const [modo, setModo] = useState(temCheckBox ? 'lista' : 'texto');

  // r172d: o valor chega async (depois do mount), então o useState inicial pega
  //        vazio → 'texto'. Re-sincroniza UMA vez quando o conteúdo carrega, pra
  //        reabrir em modo lista quando a descrição salva já é uma checklist.
  const jaSincronizou = useRef(false);
  useEffect(() => {
    if (!jaSincronizou.current && (valor || '').length > 0) {
      jaSincronizou.current = true;
      if (/^- \[[ x]\] /m.test(valor)) setModo('lista');
    }
  }, [valor]);

  // r172e: por padrão exibe ~5 linhas; botão expande pra ver o conteúdo completo
  const [expandido, setExpandido] = useState(false);

  // Toggle entre texto livre e lista
  const alternar = () => setModo(m => m === 'texto' ? 'lista' : 'texto');

  // Em modo lista: parseia linhas
  const linhas = (valor || '').split('\n');
  const itensLista = linhas.map((l, i) => {
    const m = l.match(/^- \[([ x])\] (.*)$/);
    return m
      ? { i, tipo: 'check', marcado: m[1] === 'x', texto: m[2] }
      : { i, tipo: 'texto', texto: l };
  });

  // Atualiza o checkbox no índice — substitui só aquela linha
  const toggleItem = (idx) => {
    if (disabled) return;
    const novasLinhas = linhas.map((l, i) => {
      if (i !== idx) return l;
      const m = l.match(/^- \[([ x])\] (.*)$/);
      if (!m) return l;
      const marcado = m[1] === 'x';
      return `- [${marcado ? ' ' : 'x'}] ${m[2]}`;
    });
    onChange(novasLinhas.join('\n'));
  };

  // Atualiza o texto do item
  const editarItem = (idx, novoTexto) => {
    if (disabled) return;
    const novasLinhas = linhas.map((l, i) => {
      if (i !== idx) return l;
      const m = l.match(/^- \[([ x])\] /);
      return m ? `- [${m[1]}] ${novoTexto}` : novoTexto;
    });
    onChange(novasLinhas.join('\n'));
  };

  // Remove item
  const removerItem = (idx) => {
    if (disabled) return;
    const novasLinhas = linhas.filter((_, i) => i !== idx);
    onChange(novasLinhas.join('\n'));
  };

  // r200: numeração sugerida de dois dígitos no item novo ("01 - ", "02 - ").
  // Usa o MAIOR número já presente, e não a contagem: apagar um item do meio
  // não pode fazer o próximo repetir um número que ainda existe na lista.
  const proximoNumero = () => {
    let maior = 0;
    itensLista.forEach(it => {
      const m = (it.texto || '').match(/^(\d{1,3})\s*-\s/);
      if (m) maior = Math.max(maior, parseInt(m[1], 10));
    });
    return String(maior + 1).padStart(2, '0');
  };

  // Item recém-criado nasce focado, com o cursor depois do "NN - " sugerido.
  const focarUltimo = useRef(false);

  // Adiciona item de checklist no fim, já numerado
  const adicionarItem = () => {
    if (disabled) return;
    const novo = (valor || '').trim();
    const sep = novo ? '\n' : '';
    focarUltimo.current = true;
    onChange(novo + sep + `- [ ] ${proximoNumero()} - `);
  };

  return (
    <div>
      {modo === 'texto' ? (
        <textarea
          value={valor || ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          style={{ ...textareaStyle, minHeight: 'unset', height: expandido ? '380px' : '116px', resize: 'none' }}
          placeholder={placeholder || 'Detalhes da tarefa (opcional)'}
        />
      ) : (
        <div style={{
          border: `1px solid ${COR.bordaCampo}`, borderRadius: '5px',
          backgroundColor: COR.branco,
          padding: '8px 10px',
          minHeight: '116px',
          maxHeight: expandido ? 'none' : '190px',
          overflowY: expandido ? 'visible' : 'auto',
          fontFamily: "'Manrope', sans-serif",
        }}>
          {itensLista.length === 0 || (itensLista.length === 1 && !itensLista[0].texto) ? (
            <div style={{
              fontSize: '12px', color: COR.muted, fontStyle: 'italic',
              padding: '4px 0',
            }}>
              Lista vazia. Clique em "+ Adicionar item" pra começar.
            </div>
          ) : itensLista.map(item => (
            <div key={item.i} style={{
              display: 'flex', alignItems: 'flex-start', gap: '8px',
              padding: '3px 0', fontSize: '13px',
            }}>
              {item.tipo === 'check' ? (
                <>
                  <input
                    type="checkbox"
                    checked={item.marcado}
                    onChange={() => toggleItem(item.i)}
                    disabled={disabled}
                    style={{
                      marginTop: '4px', cursor: disabled ? 'default' : 'pointer',
                      flexShrink: 0,
                    }}
                  />
                  <input
                    type="text"
                    value={item.texto}
                    onChange={e => editarItem(item.i, e.target.value)}
                    disabled={disabled}
                    ref={el => {
                      // r200: foca só o item que acabou de ser adicionado
                      if (!el || !focarUltimo.current) return;
                      if (item.i !== itensLista.length - 1) return;
                      focarUltimo.current = false;
                      el.focus();
                      el.setSelectionRange(el.value.length, el.value.length);
                    }}
                    style={{
                      flex: 1, border: 'none', outline: 'none',
                      backgroundColor: 'transparent',
                      fontSize: '13px',
                      color: item.marcado ? COR.muted : COR.texto,
                      textDecoration: item.marcado ? 'line-through' : 'none',
                      fontFamily: "'Manrope', sans-serif",
                      padding: '2px 0',
                    }}
                    placeholder="Item da lista..."
                  />
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removerItem(item.i)}
                      title="Remover item"
                      style={{
                        border: 'none', background: 'transparent',
                        cursor: 'pointer', color: COR.muted,
                        fontSize: '14px', padding: '0 4px',
                      }}
                    >×</button>
                  )}
                </>
              ) : (
                // Texto solto entre/antes dos itens — mostra como subtítulo
                <input
                  type="text"
                  value={item.texto}
                  onChange={e => editarItem(item.i, e.target.value)}
                  disabled={disabled}
                  style={{
                    flex: 1, border: 'none', outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '13px', color: COR.texto, fontWeight: '500',
                    fontFamily: "'Manrope', sans-serif",
                    padding: '2px 0',
                  }}
                  placeholder="Texto..."
                />
              )}
            </div>
          ))}

          {!disabled && (
            <button
              type="button"
              onClick={adicionarItem}
              style={{
                marginTop: '6px',
                padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                border: `1px dashed ${COR.azul}`, borderRadius: '4px',
                backgroundColor: 'transparent', color: COR.azul,
                cursor: 'pointer',
                fontFamily: "'Manrope', sans-serif",
              }}
            >+ Adicionar item</button>
          )}
        </div>
      )}
      {/* r172d/e: barra inferior — Expandir/Recolher à esquerda, toggle de modo à direita.
          r199b: os anexos da tarefa (`rodapeExtra`) entram logo ao lado do Expandir. */}
      <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between',
                    gap: '6px', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <button
            type="button"
            onClick={() => setExpandido(e => !e)}
            title={expandido ? 'Recolher pra ~5 linhas' : 'Expandir pra ver todo o conteúdo'}
            style={{
              padding: '3px 10px', fontSize: '11px', fontWeight: '600',
              border: `1px solid ${COR.borda}`, borderRadius: '4px',
              backgroundColor: COR.cinzaBg, color: COR.muted, cursor: 'pointer',
              fontFamily: "'Manrope', sans-serif",
            }}
          >{expandido ? '▲ Recolher' : '▼ Expandir'}</button>
          {rodapeExtra}
        </div>
        <button
          type="button"
          onClick={alternar}
          disabled={disabled}
          title={modo === 'texto'
            ? 'Trocar pra modo lista (com checkboxes)'
            : 'Voltar pra modo texto livre'}
          style={{
            padding: '3px 10px', fontSize: '11px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '4px',
            backgroundColor: COR.cinzaBg, color: COR.muted,
            cursor: disabled ? 'default' : 'pointer',
            fontFamily: "'Manrope', sans-serif",
          }}
        >{modo === 'texto' ? '☑ Modo Lista' : '☰ Modo Texto'}</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TarefaModal — componente principal
// ════════════════════════════════════════════════════════════════════════════
// r242: bloquearEdicao — abre em leitura e esconde o atalho "Editar". Usado
// por quem chega na tarefa de fora do módulo (ex.: botão Tarefa no Modal de
// Lançamento de Horas), onde o acesso é só pra consulta.
function TarefaModal({ aberto, modo, tarefa, tarefaMae, onFechar, onSalvar, onAtualizar, onTrocarModoEditar, bloquearEdicao = false }) {
  const { usuario } = useAuth();
  const { isAdmin, isBootstrap, pode } = usePermissoes();    // r165: pra bypass de responsável
  const ehAdmin = isAdmin || isBootstrap;
  const [form, setForm] = useState(FORM_VAZIO);
  const [usuariosLista, setUsuariosLista] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [historico, setHistorico] = useState([]);
  const [carregandoHist, setCarregandoHist] = useState(false);

  // r215: colaboradores citados (@) nos comentários desta tarefa
  const [citados, setCitados] = useState([]);
  const [carregandoCitados, setCarregandoCitados] = useState(false);
  const recarregarCitados = async () => {
    if (!tarefa?.id) return;
    setCarregandoCitados(true);
    try {
      setCitados(await tarefaService.listarCitados(tarefa.id));
    } catch (err) {
      console.warn('Erro ao recarregar citados:', err);
    } finally {
      setCarregandoCitados(false);
    }
  };

  // Vínculos hierárquicos
  const [mae, setMae] = useState(null);
  const [filhas, setFilhas] = useState([]);
  const [carregandoVinc, setCarregandoVinc] = useState(false);
  const [novaFilhaModal, setNovaFilhaModal] = useState(false);

  // Apontamentos (Fase 5 Etapa 3)
  const [apontamentos, setApontamentos] = useState([]);
  const [resumoHoras, setResumoHoras] = useState({ horas_proprias: 0, horas_filhas: 0, horas_total: 0 });
  const [carregandoApontamentos, setCarregandoApontamentos] = useState(false);
  const [apontamentoEditando, setApontamentoEditando] = useState(undefined); // undefined=fechado, null=novo, obj=editar

  // Fase 5 Etapa 4: overlay de seleção visual no calendário
  // selecionarCalendario === true → overlay aberto
  // dadosPrePreenchidos → quando o usuário soltar o drag, vira { data_inicio, data_fim }
  const [selecionarCalendario, setSelecionarCalendario] = useState(false);
  const [dadosPrePreenchidos, setDadosPrePreenchidos] = useState(null);

  const [refreshCalendario, setRefreshCalendario] = useState(0);  // pra forçar reload do CalendarioCapacidade
  // r172: status exibido localmente — mudar status NÃO fecha o modal (atualiza aqui)
  const [statusLocal, setStatusLocal] = useState(null);

  // r31c (2.13): tarefa vinculada aberta por cima (overlay sobre o próprio modal)
  // Quando o user clica numa CardVinculo, abre um TarefaModal aninhado.
  const [tarefaVinculadaAberta, setTarefaVinculadaAberta] = useState(null);

  // r199: anexos pendurados na TAREFA (os ícones do header). Diferente dos
  //       anexos de comentário, que continuam vivendo dentro do chat.
  const [anexos, setAnexos] = useState([]);
  const [anexosModal, setAnexosModal] = useState(false);

  // Seções colapsáveis
  const [sec, setSec] = useState({
    identificacao: true,
    programacao:   false,
    apontamentos:  false,
    vinculos:      true,
    citados:       true,   // r215
    historico:     false,
  });
  const toggle = (k) => setSec(s => ({ ...s, [k]: !s[k] }));

  const somenteLeitura = modo === 'visualizar';
  const ehNova         = modo === 'novo';

  // ── Carrega lista de usuários ELEGÍVEIS como responsável ────────────────
  // Pela regra 2.3: só usuários vinculados a um colaborador podem aparecer.
  useEffect(() => {
    if (!aberto) return;
    tarefaService.buscarResponsaveis()
      .then(lista => setUsuariosLista(lista))
      .catch(err => console.warn('Erro ao listar responsáveis:', err));
  }, [aberto]);

  // ── Inicializa form ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!aberto) return;
    setErro('');
    setSec({ identificacao: true, programacao: false, apontamentos: false, vinculos: true, historico: false });
    setMae(null);
    setFilhas([]);
    setApontamentos([]);
    setAnexos([]);   // r199
    setResumoHoras({ horas_proprias: 0, horas_filhas: 0, horas_total: 0 });

    if (ehNova) {
      const baseForm = { ...FORM_VAZIO };
      // 2.4: responsável default = solicitante (usuário logado).
      // Se ele for elegível (vinculado a colaborador), aparecerá no combo;
      // senão fica vazio.
      if (usuario?.id) {
        baseForm.responsavel_id = usuario.id;
      }
      // Herança quando criando filha (clica "+ Nova sub-tarefa" no pai)
      if (tarefaMae) {
        // r166: log de debug pra confirmar o que veio do pai. Verifique o console
        //       do browser (F12) ao criar uma sub-tarefa. Se `tem_descricao=false`,
        //       o objeto mãe passado como prop não tem o campo — problema é no pai.
        console.log('[r166] Nova sub-tarefa herdando de:', {
          id: tarefaMae.id,
          titulo: tarefaMae.titulo,
          tem_descricao: !!tarefaMae.descricao,
          descricao_preview: tarefaMae.descricao
            ? String(tarefaMae.descricao).substring(0, 80)
            : '(vazia ou não veio)',
        });

        if (tarefaMae.cliente_id) {
          baseForm.cliente_id      = tarefaMae.cliente_id;
          baseForm.cliente_nome    = tarefaMae.cliente_fantasia || tarefaMae.cliente_nome || '';
          baseForm.cliente_tag     = tarefaMae.cliente_tag || '';
          baseForm.cliente_cor_tag = tarefaMae.cliente_cor_tag || '';
        }
        if (tarefaMae.negocio_id) {
          baseForm.negocio_id    = tarefaMae.negocio_id;
          baseForm.negocio_label = '📂 ' + tarefaMae.negocio_id +
            (tarefaMae.negocio_descricao ? ' — ' + tarefaMae.negocio_descricao : '');
        }
        if (tarefaMae.setor) baseForm.setor = tarefaMae.setor;
        // r166: herda descrição da mãe (user pode editar antes de salvar).
        //       Aceita string vazia como "não herda" mas trata null/undefined.
        if (tarefaMae.descricao && String(tarefaMae.descricao).trim() !== '') {
          baseForm.descricao = String(tarefaMae.descricao);
        }
        // Guarda a mãe pra exibir na seção de vínculos
        setMae({
          id: tarefaMae.id, titulo: tarefaMae.titulo,
          status_execucao: tarefaMae.status_execucao,
          responsavel_nome: tarefaMae.responsavel_nome, deadline: tarefaMae.deadline,
        });
      }
      setForm(baseForm);
      setHistorico([]);
      setStatusLocal(null);
    } else if (tarefa) {
      setStatusLocal(tarefa.status_execucao);
      setForm({
        titulo:                  tarefa.titulo || '',
        descricao:               tarefa.descricao || '',
        chamado:                 tarefa.chamado || '',
        setor:                   tarefa.setor || 'C',
        prioridade:              tarefa.prioridade || 'B',
        responsavel_id:          tarefa.responsavel_id || '',
        deadline:                toDateLocal(tarefa.deadline),
        cliente_id:              tarefa.cliente_id || '',
        cliente_nome:            tarefa.cliente_fantasia || tarefa.cliente_nome || '',
        cliente_tag:             tarefa.cliente_tag || '',
        cliente_cor_tag:         tarefa.cliente_cor_tag || '',
        negocio_id:              tarefa.negocio_id || '',
        // r35 item 1: mostrar código + DESCRIÇÃO do projeto (não cliente)
        negocio_label:           tarefa.negocio_id
          ? ('📂 ' + tarefa.negocio_id + (tarefa.negocio_descricao ? ' — ' + tarefa.negocio_descricao : ''))
          : '',
      });

      setCarregandoHist(true);
      tarefaService.listarAnotacoes(tarefa.id)
        .then(setHistorico)
        .catch(err => console.warn('Erro ao carregar histórico:', err))
        .finally(() => setCarregandoHist(false));

      // Carrega mãe (se existe) e filhas
      setCarregandoVinc(true);
      const promFilhas = tarefaService.listarFilhas(tarefa.id)
        .then(setFilhas)
        .catch(err => console.warn('Erro ao carregar filhas:', err));
      const promMae = tarefa.tarefa_mae_id
        ? tarefaService.buscarPorId(tarefa.tarefa_mae_id, usuario?.id)
            .then(m => setMae(m))
            .catch(() => {})
        : Promise.resolve();
      Promise.all([promFilhas, promMae]).finally(() => setCarregandoVinc(false));

      // r215: colaboradores citados (@) nos comentários. Best-effort — a lista
      //       é informativa e não pode derrubar o modal.
      setCarregandoCitados(true);
      tarefaService.listarCitados(tarefa.id)
        .then(setCitados)
        .catch(err => console.warn('Erro ao carregar citados:', err))
        .finally(() => setCarregandoCitados(false));

      // Carrega apontamentos da tarefa + resumo de horas
      setCarregandoApontamentos(true);
      Promise.all([
        apontamentoService.listar(tarefa.id).then(setApontamentos).catch(err => {
          console.warn('Erro ao carregar apontamentos:', err);
        }),
        apontamentoService.resumo(tarefa.id).then(setResumoHoras).catch(() => {}),
      ]).finally(() => setCarregandoApontamentos(false));

      // r199: anexos da tarefa (ícones do header). Best-effort: falhar aqui
      //       não pode impedir o modal de abrir.
      tarefaAnexoService.listar(tarefa.id)
        .then(setAnexos)
        .catch(err => console.warn('Erro ao carregar anexos da tarefa:', err));
    }
  }, [aberto, ehNova, tarefa, tarefaMae]);

  const handleCampo = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── Salvar (POST ou PUT) ─────────────────────────────────────────────────
  const salvar = useCallback(async () => {
    setErro('');
    if (!form.titulo.trim())  return setErro('Título é obrigatório.');
    if (!form.setor)          return setErro('Setor é obrigatório.');
    if (!form.prioridade)     return setErro('Prioridade é obrigatória.');

    const payload = {
      titulo:                 form.titulo.trim(),
      descricao:              form.descricao.trim() || null,
      chamado:                form.chamado.trim() || null,
      setor:                  form.setor,
      prioridade:             form.prioridade,
      responsavel_id:         form.responsavel_id || null,
      deadline:               form.deadline || null,
      cliente_id:             form.cliente_id || null,
      negocio_id:             form.negocio_id || null,
      // tarefa-mãe só vem se a tarefa foi criada via "+ Nova sub-tarefa"
      tarefa_mae_id:          (ehNova && tarefaMae) ? tarefaMae.id : (tarefa?.tarefa_mae_id || null),
      usuario_id:             usuario?.id,
    };

    try {
      setSalvando(true);
      if (ehNova) {
        payload.solicitante_id = usuario?.id;
        payload.status_execucao = form.status_execucao || 'N';   // r172: status escolhido na inclusão
        await tarefaService.incluir(payload);
      } else {
        // r178: prioridade mudou e o responsável é outra pessoa? oferece o
        //       alerta de solicitação de priorização.
        const respAtual = form.responsavel_id || tarefa?.responsavel_id || null;
        if (tarefa?.prioridade && form.prioridade !== tarefa.prioridade
            && respAtual && respAtual !== usuario?.id) {
          payload.solicitar_prioridade = window.confirm(
            'Deseja gerar um alerta de solicitação de prioridade para o responsável?'
          );
        }
        await tarefaService.alterar(tarefa.id, payload);
      }
      onSalvar();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao salvar tarefa.');
    } finally {
      setSalvando(false);
    }
  }, [form, ehNova, tarefa, tarefaMae, usuario, onSalvar]);

  // ── Botões de status ─────────────────────────────────────────────────────
  const alterarStatus = async (novoStatus, force = false) => {
    if (!tarefa) return;
    try {
      await tarefaService.alterarStatusExecucao(tarefa.id, novoStatus, usuario?.id, force);
      // r172: NÃO fecha o modal — atualiza o status exibido e recarrega a lista em segundo plano
      setStatusLocal(novoStatus);
      if (onAtualizar) onAtualizar();
    } catch (err) {
      const resp = err.response;
      if (resp?.status === 409 && resp.data?.filhas_abertas) {
        const qtd = resp.data.filhas_abertas;
        const ok = window.confirm(
          `Esta tarefa tem ${qtd} sub-tarefa(s) em aberto.\n\nConcluir mesmo assim?`
        );
        if (ok) await alterarStatus(novoStatus, true);
        return;
      }
      alert(resp?.data?.mensagem || 'Erro ao alterar status.');
    }
  };

  // ── Excluir ──────────────────────────────────────────────────────────────
  const excluir = async () => {
    if (!tarefa) return;
    if (!window.confirm(`Excluir a tarefa #${tarefa.id} "${tarefa.titulo}"?`)) return;
    try {
      await tarefaService.excluir(tarefa.id, usuario?.id);
      onSalvar();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao excluir tarefa.');
    }
  };

  // ── Recarregar filhas (depois que cria uma nova via botão) ───────────────
  const recarregarFilhas = async () => {
    if (!tarefa) return;
    try {
      setCarregandoVinc(true);
      const novas = await tarefaService.listarFilhas(tarefa.id);
      setFilhas(novas);
    } finally {
      setCarregandoVinc(false);
    }
  };

  // r31c (2.13): abre uma tarefa vinculada por cima (em overlay)
  // Faz buscarPorId pra trazer dados completos antes de abrir
  const abrirTarefaVinculada = async (vinc) => {
    try {
      const completa = await tarefaService.buscarPorId(vinc.id, usuario?.id);
      setTarefaVinculadaAberta(completa);
    } catch (err) {
      console.warn('Erro ao abrir tarefa vinculada:', err);
      // Fallback: abre com dados parciais
      setTarefaVinculadaAberta(vinc);
    }
  };

  // ── Apontamentos ──────────────────────────────────────────────────────
  // r185 item 5: apontar horas numa tarefa "Não Iniciada" a põe "Em Andamento"
  // no backend (r182). O modal não relia a tarefa, então continuava exibindo o
  // status antigo e parecia que a regra não tinha rodado.
  const sincronizarStatusTarefa = async () => {
    if (!tarefa) return;
    try {
      const atual = await tarefaService.buscarPorId(tarefa.id, usuario?.id);
      const novo = atual?.status_execucao;
      if (!novo) return;
      if (novo === (statusLocal ?? tarefa.status_execucao)) return;
      setStatusLocal(novo);
      if (onAtualizar) onAtualizar();
    } catch (err) {
      console.warn('Erro ao sincronizar status da tarefa:', err);
    }
  };


  // r199: recarrega os anexos depois de incluir/editar/excluir no modal
  const recarregarAnexos = async () => {
    if (!tarefa) return;
    try {
      setAnexos(await tarefaAnexoService.listar(tarefa.id));
    } catch (err) {
      console.warn('Erro ao recarregar anexos:', err);
    }
  };

  const recarregarApontamentos = async () => {
    if (!tarefa) return;
    try {
      setCarregandoApontamentos(true);
      const [lista, resumo] = await Promise.all([
        apontamentoService.listar(tarefa.id),
        apontamentoService.resumo(tarefa.id),
      ]);
      setApontamentos(lista);
      setResumoHoras(resumo);
    } catch (err) {
      console.warn('Erro ao recarregar apontamentos:', err);
    } finally {
      setCarregandoApontamentos(false);
    }
    await sincronizarStatusTarefa();
  };

  const excluirApontamento = async (apId) => {
    if (!window.confirm(
      'Excluir este apontamento? O bloco no calendário também será removido.'
    )) return;
    try {
      await apontamentoService.excluir(apId, usuario?.id);
      await recarregarApontamentos();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao excluir apontamento.');
    }
  };

  // Helper: posso editar/excluir um apontamento?
  // r165: próprio apontador OU responsável atual da tarefa OU admin.
  //       Solicitante NÃO tem mais permissão (mudou junto com o item 1).
  const podeEditarApontamento = (ap) => {
    if (!usuario || !tarefa) return false;
    if (ehAdmin) return true;
    return ap.usuario_id === usuario.id
        || tarefa.responsavel_id === usuario.id;
  };

  // r165: posso apontar horas? Só responsável ou admin.
  //       Antes: responsável OU solicitante.
  const ehResponsavel = !!(usuario && tarefa && tarefa.responsavel_id === usuario.id);
  const podeApontar = (!ehNova && tarefa && usuario && (ehResponsavel || ehAdmin));

  // r165: posso finalizar a tarefa? Mesma regra do apontar.
  const podeFinalizar = ehResponsavel || ehAdmin;

  if (!aberto) return null;

  // ── Título do header ────────────────────────────────────────────────────
  let tituloHeader = 'Nova Tarefa';
  if (modo === 'visualizar' && tarefa) tituloHeader = `Tarefa #${tarefa.id}`;
  if (modo === 'editar'      && tarefa) tituloHeader = `Editando #${tarefa.id}`;
  if (ehNova && tarefaMae) tituloHeader = `Nova sub-tarefa de #${tarefaMae.id}`;

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — modal tela cheia
  // ════════════════════════════════════════════════════════════════════════
  return (
    <>
    <div style={{
      position: 'absolute', inset: 0, zIndex: 200,
      backgroundColor: COR.cinzaBg,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      fontFamily: "'Manrope', sans-serif",
    }}>
      {/* Header (r172: padrão Cliente — azul + ícone da rotina + botões no topo) */}
      <div style={{
        backgroundColor: COR.azul,
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '14px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {iconeDaRotina('tarefas', { width: 36, height: 36, color: COR.branco })}
          </div>
          <div style={{
            fontSize: '16px', fontWeight: '700', color: COR.branco,
            lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {tituloHeader}
          </div>
        </div>

        {/* r172: todos os botões no topo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {modo === 'visualizar' && !bloquearEdicao && (
            <SePode rotina="tarefas" acao="alterar">
              <button onClick={onTrocarModoEditar} style={btnHeaderClaro}>✎ Editar</button>
            </SePode>
          )}
          {!somenteLeitura && modo === 'editar' && tarefa && (
            <SePode rotina="tarefas" acao="excluir">
              <button onClick={excluir} style={btnHeaderPerigo}>🗑 Excluir</button>
            </SePode>
          )}
          {!somenteLeitura && (
            <>
              <button onClick={onFechar} style={btnHeaderClaro}>Cancelar</button>
              <SePode rotina="tarefas" acao={ehNova ? 'incluir' : 'alterar'}>
                <button onClick={salvar} disabled={salvando}
                  style={{ ...btnHeaderSalvar, cursor: salvando ? 'wait' : 'pointer', opacity: salvando ? 0.7 : 1 }}>
                  {salvando ? 'Salvando...' : '✓ Salvar'}
                </button>
              </SePode>
            </>
          )}
          {modo === 'visualizar' && (
            <button onClick={onFechar} title="Fechar (ESC)" style={{ ...btnHeaderClaro, padding: '7px 12px' }}>×</button>
          )}
        </div>
      </div>

      {/* Conteúdo scrollável */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '16px 20px',
      }}>
        {/* Grid 2 colunas (r31c 2.13): coluna esquerda com seções principais,
            coluna direita com Tarefas Vinculadas */}
        <div style={{
          maxWidth: '1600px', margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: ehNova ? '1fr' : '65fr 35fr',   // r172f: 65% / 35%
          gap: '12px',
          alignItems: 'start',
        }}>
        <div>{/* COLUNA ESQUERDA */}

          {/* ─── Seção: IDENTIFICAÇÃO ─────────────────────────────────── */}
          <Secao icone={SvgIdentificacao} titulo="Identificação"
                 aberta={sec.identificacao} onToggle={() => toggle('identificacao')}>
            {/* r172: Status + Setor + Prioridade na mesma linha (combos de pílula).
                Status só em tarefas existentes; 'Finalizado' só se puder finalizar. */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {ehNova ? (
                /* r172: na inclusão o Status vai no form (default 'Não Iniciado'), editável */
                <Campo label="Status" auto>
                  <PillCombo
                    valor={form.status_execucao}
                    opcoes={STATUS_OPCOES}
                    onChange={(v) => setForm(f => ({ ...f, status_execucao: v }))}
                    disabled={somenteLeitura}
                  />
                </Campo>
              ) : tarefa && (
                <Campo label="Status" auto>
                  <PillCombo
                    valor={statusLocal ?? tarefa.status_execucao}
                    opcoes={STATUS_OPCOES.filter(s => s.v !== 'F' || podeFinalizar || (statusLocal ?? tarefa.status_execucao) === 'F')}
                    onChange={(v) => alterarStatus(v)}
                    disabled={modo === 'visualizar'}
                  />
                </Campo>
              )}
              <Campo label="Setor" obrigatorio auto>
                <PillCombo
                  valor={form.setor}
                  opcoes={SETORES}
                  onChange={(v) => setForm(f => ({ ...f, setor: v }))}
                  disabled={somenteLeitura}
                />
              </Campo>
              <Campo label="Prioridade" obrigatorio auto>
                <PillCombo
                  valor={form.prioridade}
                  opcoes={PRIORIDADE_OPCOES}
                  onChange={(v) => setForm(f => ({ ...f, prioridade: v }))}
                  disabled={somenteLeitura}
                />
              </Campo>
              {/* Data de Criação na linha do Status, empurrada pra direita */}
              <div style={{ marginLeft: 'auto', width: '130px' }}>
                <Campo label="Data de Criação">
                  <input type="text" readOnly disabled
                    value={tarefa?.criado_em ? new Date(tarefa.criado_em).toLocaleDateString('pt-BR') : '—'}
                    style={{ ...inputStyle, backgroundColor: COR.cinzaBg }} />
                </Campo>
              </div>
            </div>

            {/* Título (sem prioridade embutida — prioridade fica nas tags abaixo) */}
            <Campo label="Título" obrigatorio>
              <input type="text" name="titulo" value={form.titulo} onChange={handleCampo}
                     disabled={somenteLeitura} maxLength={200}
                     style={{ ...inputStyle, width: '100%' }}
                     placeholder="O que precisa ser feito?" />
            </Campo>

            {/* Cliente + Projeto — comboboxes inline (sem modal por cima) */}
            {/* Cliente | Projeto (Nº do Chamado desceu pra linha do Solicitante) */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <Campo label="Cliente" metade>
                <ComboboxInline
                  valor={form.cliente_id}
                  labelAtual={form.cliente_nome}
                  rawAtual={form.cliente_id ? {
                    id: form.cliente_id, nome_fantasia: form.cliente_nome,
                    tag: form.cliente_tag, cor_tag: form.cliente_cor_tag,
                  } : null}
                  renderSelecionado={(c) => (
                    <>
                      <TagCliente tag={c?.tag} cor={c?.cor_tag}
                                  titulo={c?.nome_fantasia} />
                      <span style={{ fontSize: '13px', color: COR.texto, fontWeight: '600', flex: 1 }}>
                        {c?.nome_fantasia}
                      </span>
                    </>
                  )}
                  buscar={(termo) => tarefaService.buscarClientes(termo)}
                  getId={(c) => c.id}
                  getLabel={(c) => c.nome_fantasia || c.razao_social}
                  renderItem={(c) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <TagCliente tag={c.tag} cor={c.cor_tag}
                                  titulo={c.nome_fantasia || c.razao_social} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: COR.texto }}>
                          {c.nome_fantasia || c.razao_social}
                        </div>
                        {c.nome_fantasia && (
                          <div style={{ fontSize: '10px', color: COR.muted }}>
                            {c.razao_social}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  onSelecionar={(sel) => {
                    setForm(f => ({
                      ...f,
                      cliente_id:      sel?.id || '',
                      cliente_nome:    sel?.raw?.nome_fantasia || sel?.raw?.razao_social || '',
                      cliente_tag:     sel?.raw?.tag || '',
                      cliente_cor_tag: sel?.raw?.cor_tag || '',
                      // Trocou de cliente? Limpa o projeto vinculado
                      negocio_id:      f.cliente_id !== (sel?.id || '') ? '' : f.negocio_id,
                      negocio_label:   f.cliente_id !== (sel?.id || '') ? '' : f.negocio_label,
                    }));
                  }}
                  placeholder="Nome fantasia, razão social ou CNPJ..."
                  desabilitado={somenteLeitura}
                  estiloTexto={estiloTextoCombo}
                />
              </Campo>

              <Campo label="Projeto (Negócio)" metade>
                <ComboboxInline
                  valor={form.negocio_id}
                  labelAtual={form.negocio_label}
                  buscar={(termo) => tarefaService.buscarNegocios(termo, form.cliente_id)}
                  getId={(n) => n.id}
                  getLabel={(n) => `📂 ${n.id}${n.descricao ? ' — ' + n.descricao : ''}`}
                  renderItem={(n) => (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: COR.texto }}>
                        📂 {n.id}{n.descricao ? ` — ${n.descricao}` : ''}
                      </div>
                      {n.cliente_nome && (
                        <div style={{ fontSize: '10px', color: COR.muted, marginTop: '2px' }}>
                          {n.cliente_nome}
                        </div>
                      )}
                    </div>
                  )}
                  onSelecionar={(sel) => {
                    setForm(f => {
                      const novo = {
                        ...f,
                        negocio_id:    sel?.id || '',
                        negocio_label: sel ? `📂 ${sel.raw.id}${sel.raw.descricao ? ' — ' + sel.raw.descricao : ''}` : '',
                      };
                      // Auto-preenche cliente se vier negócio sem cliente
                      if (sel?.raw?.cliente_id && !f.cliente_id) {
                        novo.cliente_id   = sel.raw.cliente_id;
                        novo.cliente_nome = sel.raw.cliente_nome || '';
                      }
                      return novo;
                    });
                  }}
                  placeholder={form.cliente_id
                    ? 'Projetos deste cliente...'
                    : 'Todos os projetos...'}
                  desabilitado={somenteLeitura}
                  estiloTexto={estiloTextoCombo}
                />
              </Campo>
            </div>

            {/* Nº do Chamado | Solicitante | Responsável | Deadline na mesma linha */}
            <div style={{ display: 'flex', gap: '12px' }}>
              {/* r215: número do chamado no sistema do cliente. Texto livre —
                  cada cliente numera do seu jeito (ERP-1658, #4432, INC00123).
                  Entra na busca textual do browse. */}
              <Campo label="Nº do Chamado" quarto>
                <input type="text" name="chamado" value={form.chamado} onChange={handleCampo}
                       disabled={somenteLeitura} maxLength={40}
                       style={{ ...inputStyle, width: '100%' }}
                       placeholder="Ex.: ERP-1658" />
              </Campo>
              <Campo label="Solicitante" quarto>
                <input type="text"
                  value={ehNova
                    ? (usuario?.nomeAmigavel || usuario?.nome || '')
                    : (tarefa?.solicitante_nome || tarefa?.solicitante_id || '')}
                  readOnly disabled
                  style={{ ...inputStyle, backgroundColor: COR.cinzaBg }} />
              </Campo>
              <Campo label="Responsável" quarto>
                <select name="responsavel_id" value={form.responsavel_id} onChange={handleCampo}
                        disabled={somenteLeitura} style={selectStyle}>
                  <option value="">— Sem atribuição —</option>
                  {usuariosLista.map(u => (
                    <option key={u.id} value={u.id}>{u.nome_amigavel || u.nome}</option>
                  ))}
                </select>
              </Campo>
              <Campo label="Deadline" quarto>
                <input type="date" name="deadline" value={form.deadline}
                       onChange={handleCampo} disabled={somenteLeitura} style={inputStyle} />
              </Campo>
            </div>

            {/* 2.10: Descrição é o ÚLTIMO campo da seção */}
            {/* r32 item 2: textarea com toggle Texto livre / Lista (checkboxes) */}
            <Campo label="Descrição">
              <DescricaoChecklist
                valor={form.descricao}
                onChange={(v) => setForm(f => ({ ...f, descricao: v }))}
                disabled={somenteLeitura}
                /* r199b: anexos da tarefa na barra de baixo, ao lado do Expandir.
                   Só em tarefa já existente — anexo precisa de tarefa gravada. */
                rodapeExtra={!ehNova && tarefa ? (
                  <>
                    <button type="button" onClick={() => setAnexosModal(true)}
                      title={anexos.length ? `${anexos.length} anexo(s) — clique para gerenciar`
                                           : 'Nenhum anexo — clique para incluir'}
                      style={{
                        padding: '3px 10px', fontSize: '11px', fontWeight: '600',
                        border: `1px solid ${COR.borda}`, borderRadius: '4px',
                        backgroundColor: COR.cinzaBg, color: COR.muted, cursor: 'pointer',
                        fontFamily: "'Manrope', sans-serif", whiteSpace: 'nowrap',
                      }}>
                      📎 Anexos{anexos.length ? ` (${anexos.length})` : ''}
                    </button>
                    <AnexosCabecalho anexos={anexos} onGerenciar={() => setAnexosModal(true)} />
                  </>
                ) : null}
                placeholder="Detalhes da tarefa (opcional). Use o toggle pra trocar pra checklist."
              />
            </Campo>

            {/* r172: campos de programação (início/horas/fim) e o botão de
                selecionar início no calendário foram removidos. */}
          </Secao>

          {/* r172e: seção Apontamentos movida para a coluna direita (abaixo de Subtarefas) */}

          {/* ─── Seção: COMENTÁRIOS (r35 item 6) ───────────────────── */}
          {!ehNova && tarefa && (
            <Secao icone={SvgComentarios} titulo="Comentários"
                   aberta={sec.comentarios !== false}
                   onToggle={() => toggle('comentarios')}>
              <ChatComentarios
                tarefaId={tarefa.id}
                podeComentar={modo !== 'visualizar' || true}  /* sempre pode comentar quem vê */
                /* r213: sem "mencionaveis" — o chat carrega TODOS os usuários
                   ativos por conta própria (antes era só solicitante/responsável) */
              />
            </Secao>
          )}

          {/* ─── Seção: HISTÓRICO ───────────────────────────────────── */}
          {!ehNova && (
            <Secao icone={SvgHistorico} titulo="Histórico"
                   aberta={sec.historico} onToggle={() => toggle('historico')}>
              {carregandoHist ? (
                <div style={{ fontSize: '11px', color: COR.muted }}>Carregando...</div>
              ) : historico.length === 0 ? (
                <div style={{ fontSize: '11px', color: COR.muted }}>Sem eventos.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {historico.map(h => (
                    <div key={h.id} style={{
                      fontSize: '11px', padding: '6px 10px',
                      backgroundColor: COR.cinzaBg, borderRadius: '4px',
                      display: 'flex', gap: '8px', flexWrap: 'wrap',
                    }}>
                      <span style={{ fontWeight: '600', color: COR.azul, minWidth: '110px' }}>
                        {h.tipo_evento}
                      </span>
                      {h.descricao && <span style={{ color: COR.texto }}>{h.descricao}</span>}
                      <span style={{ marginLeft: 'auto', color: COR.muted }}>
                        {h.usuario_nome || h.usuario_id}
                        {' · '}
                        {new Date(h.data_evento).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Secao>
          )}

          {/* Erro */}
          {erro && (
            <div style={{
              marginTop: '10px', padding: '10px 14px',
              backgroundColor: COR.vermBg, border: `1px solid ${COR.vermelho}`,
              borderRadius: '5px', color: COR.vermelho, fontSize: '12px', fontWeight: '600',
            }}>{erro}</div>
          )}
        </div>{/* /COLUNA ESQUERDA */}

        {/* ─── COLUNA DIREITA: TAREFAS VINCULADAS + APONTAMENTOS (r172e) ─── */}
        {!ehNova && (
        <div>
          <Secao
            icone={SvgVinculos} titulo="Tarefas Vinculadas"
            aberta={sec.vinculos} onToggle={() => toggle('vinculos')}
            acao={!somenteLeitura && !ehNova && tarefa ? (
              <button
                onClick={() => setNovaFilhaModal(true)}
                style={{
                  padding: '4px 9px', fontSize: '10px', fontWeight: '600',
                  border: `1px solid ${COR.azul}`, borderRadius: '4px',
                  backgroundColor: COR.azul, color: COR.branco, cursor: 'pointer',
                  fontFamily: "'Manrope', sans-serif",
                }}
              >+ Sub-tarefa</button>
            ) : null}
          >
            {carregandoVinc ? (
              <div style={{ textAlign: 'center', padding: '16px', color: COR.muted, fontSize: '12px' }}>
                Carregando...
              </div>
            ) : (
              <>
                {!mae && filhas.length === 0 && (
                  <div style={{
                    textAlign: 'center', padding: '20px',
                    color: COR.muted, fontSize: '12px', fontStyle: 'italic',
                    backgroundColor: COR.cinzaBg, borderRadius: '5px',
                  }}>
                    Nenhum vínculo. Use "+ Sub-tarefa" pra criar.
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* Mãe (se houver) — primeiro */}
                  {mae && (
                    <CardVinculo
                      tarefa={mae}
                      flag="MÃE" flagCor={COR.roxo}
                      onAbrir={abrirTarefaVinculada}
                    />
                  )}
                  {/* Filhas */}
                  {filhas.map(f => (
                    <CardVinculo
                      key={f.id}
                      tarefa={f}
                      flag="FILHA" flagCor={COR.verde}
                      onAbrir={abrirTarefaVinculada}
                    />
                  ))}
                </div>
              </>
            )}
          </Secao>

          {/* ─── r215: Seção COLABORADORES CITADOS ─────────────────────────
              Quem foi citado com @ nos comentários desta tarefa. Uma linha por
              pessoa (não por citação), da citação mais recente pra mais antiga.
              O botão de recarregar existe porque a lista é buscada ao abrir o
              modal — citar alguém no chat logo abaixo não a atualiza sozinha. */}
          <Secao
            icone={SvgCitados}
            titulo={`Colaboradores Citados${citados.length ? ` (${citados.length})` : ''}`}
            aberta={sec.citados} onToggle={() => toggle('citados')}
            acao={tarefa ? (
              <button
                onClick={recarregarCitados}
                disabled={carregandoCitados}
                title="Recarregar a lista de citados"
                style={{
                  padding: '4px 9px', fontSize: '10px', fontWeight: '600',
                  border: `1px solid ${COR.borda}`, borderRadius: '4px',
                  backgroundColor: COR.cinzaBg, color: COR.muted,
                  cursor: carregandoCitados ? 'default' : 'pointer',
                  fontFamily: "'Manrope', sans-serif",
                }}
              >↻ Atualizar</button>
            ) : null}
          >
            {carregandoCitados ? (
              <div style={{ textAlign: 'center', padding: '16px', color: COR.muted, fontSize: '12px' }}>
                Carregando...
              </div>
            ) : citados.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '20px',
                color: COR.muted, fontSize: '12px', fontStyle: 'italic',
                backgroundColor: COR.cinzaBg, borderRadius: '5px',
              }}>
                Ninguém citado ainda. Use "@" nos comentários pra citar.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {citados.map(c => (
                  <div key={c.usuario_id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 10px',
                    border: `1px solid ${COR.borda}`, borderRadius: '5px',
                    backgroundColor: COR.branco,
                  }}>
                    <AvatarCitado nome={c.nome} foto={c.foto} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '12px', fontWeight: '700', color: COR.texto,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={c.email || ''}>
                        {c.nome || c.usuario_id}
                      </div>
                      <div style={{ fontSize: '10px', color: COR.muted }}>
                        {c.citacoes > 1 ? `${c.citacoes} citações` : '1 citação'}
                        {c.ultima_citacao
                          ? ` · última em ${new Date(c.ultima_citacao).toLocaleDateString('pt-BR')}`
                          : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Secao>

          {/* ─── Seção: APONTAMENTOS DE HORAS — r172e: na lateral, abaixo de Subtarefas ─── */}
          <Secao
            icone={SvgApontamentos} titulo={`Apontamentos (${formatarHorasResumo(resumoHoras)})`}
            aberta={sec.apontamentos} onToggle={() => toggle('apontamentos')}
            acao={podeApontar ? (
              <button
                onClick={() => setApontamentoEditando(null)}
                style={{
                  padding: '5px 11px', fontSize: '11px', fontWeight: '600',
                  border: `1px solid ${COR.azul}`, borderRadius: '4px',
                  backgroundColor: COR.azul, color: COR.branco, cursor: 'pointer',
                  fontFamily: "'Manrope', sans-serif",
                }}
              >+ Apontar</button>
            ) : null}
          >
            {/* Resumo de horas no topo */}
            <div style={{
              padding: '10px 14px', marginBottom: '12px',
              backgroundColor: COR.azulBg,
              border: `1px solid ${COR.azul}33`,
              borderRadius: '5px',
              display: 'flex', gap: '24px', flexWrap: 'wrap',
              fontSize: '12px',
            }}>
              <div>
                <div style={{ color: COR.muted, fontSize: '10px', fontWeight: '600' }}>
                  PRÓPRIAS
                </div>
                <div style={{ color: COR.azul, fontSize: '15px', fontWeight: '700' }}>
                  {Number(resumoHoras.horas_proprias || 0).toFixed(2)}h
                </div>
              </div>
              <div>
                <div style={{ color: COR.muted, fontSize: '10px', fontWeight: '600' }}>
                  SUB-TAREFAS
                </div>
                <div style={{ color: COR.azul, fontSize: '15px', fontWeight: '700' }}>
                  {Number(resumoHoras.horas_filhas || 0).toFixed(2)}h
                </div>
              </div>
              <div>
                <div style={{ color: COR.muted, fontSize: '10px', fontWeight: '600' }}>
                  TOTAL
                </div>
                <div style={{ color: COR.texto, fontSize: '15px', fontWeight: '700' }}>
                  {Number(resumoHoras.horas_total || 0).toFixed(2)}h
                </div>
              </div>
            </div>

            {carregandoApontamentos ? (
              <div style={{ textAlign: 'center', padding: '16px', color: COR.muted, fontSize: '12px' }}>
                Carregando...
              </div>
            ) : apontamentos.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '20px',
                color: COR.muted, fontSize: '12px', fontStyle: 'italic',
                backgroundColor: COR.cinzaBg, borderRadius: '5px',
              }}>
                {podeApontar
                  ? 'Nenhum apontamento ainda. Clique em "+ Apontar" pra registrar.'
                  : 'Nenhum apontamento. Só responsável e solicitante podem apontar.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {apontamentos.map(ap => (
                  <LinhaApontamento
                    key={ap.id}
                    apontamento={ap}
                    podeEditar={podeEditarApontamento(ap)}
                    onEditar={() => setApontamentoEditando(ap)}
                    onExcluir={() => excluirApontamento(ap.id)}
                  />
                ))}
              </div>
            )}
          </Secao>
        </div>
        )}
        </div>{/* /GRID */}
      </div>

    </div>

    {/* Modal recursivo: "Nova sub-tarefa" */}
    {novaFilhaModal && tarefa && (
      <TarefaModal
        aberto={true}
        modo="novo"
        tarefaMae={tarefa}
        onFechar={() => setNovaFilhaModal(false)}
        onSalvar={async () => {
          setNovaFilhaModal(false);
          await recarregarFilhas();
        }}
      />
    )}

    {/* r31c (2.13): tarefa vinculada aberta por cima */}
    {tarefaVinculadaAberta && (
      <TarefaModal
        aberto={true}
        modo="visualizar"
        tarefa={tarefaVinculadaAberta}
        onFechar={() => setTarefaVinculadaAberta(null)}
        onTrocarModoEditar={() => {/* trocar pra edicao se quiser editar */}}
        onSalvar={async () => {
          setTarefaVinculadaAberta(null);
          await recarregarFilhas();
        }}
      />
    )}

    {/* Modal de apontamento (novo ou edição) */}
    {apontamentoEditando !== undefined && tarefa && (
      <ApontamentoModal
        aberto={true}
        tarefaId={tarefa.id}
        tarefaTitulo={tarefa.titulo}
        apontamento={apontamentoEditando}
        dadosIniciais={dadosPrePreenchidos}
        onFechar={() => {
          setApontamentoEditando(undefined);
          setDadosPrePreenchidos(null);
        }}
        onSalvar={async () => {
          setApontamentoEditando(undefined);
          setDadosPrePreenchidos(null);
          await recarregarApontamentos();
        }}
      />
    )}

    {/* Overlay de seleção visual no calendário (Etapa 4) */}
    {selecionarCalendario && tarefa && (
      <CalendarioSelecaoOverlay
        aberto={true}
        tarefaId={tarefa.id}
        tarefaTitulo={tarefa.titulo}
        onFechar={() => setSelecionarCalendario(false)}
        onSelecionar={({ start, end }) => {
          // Fecha o overlay e abre o ApontamentoModal pré-preenchido com as datas
          setSelecionarCalendario(false);
          setDadosPrePreenchidos({
            data_inicio: start.toISOString(),
            data_fim:    end.toISOString(),
          });
          setApontamentoEditando(null);  // null = novo apontamento
        }}
      />
    )}

    {/* r199: gerenciador de anexos da tarefa */}
    {anexosModal && tarefa && (
      <TarefaAnexosModal
        tarefaId={tarefa.id}
        anexos={anexos}
        podeAlterar={!bloquearEdicao && pode('tarefas', 'alterar')}
        onFechar={() => setAnexosModal(false)}
        onMudou={recarregarAnexos}
      />
    )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Helper: formato compacto pro título da seção (X.XXh apontadas)
// ════════════════════════════════════════════════════════════════════════════
function formatarHorasResumo(r) {
  const total = Number(r?.horas_total || 0);
  if (total === 0) return '0h';
  return `${total.toFixed(2)}h`;
}

// ════════════════════════════════════════════════════════════════════════════
// <LinhaApontamento> — uma linha na lista de apontamentos da tarefa
// ════════════════════════════════════════════════════════════════════════════
function LinhaApontamento({ apontamento, podeEditar, onEditar, onExcluir }) {
  const COR_L = {
    azul: '#003399', borda: '#e8eaed', texto: '#1a1a2e', muted: '#888',
    branco: '#fff', cinzaBg: '#fafafa',
    vermelho: '#dc2626', verde: '#10b981',
  };
  const ini = new Date(apontamento.data_inicio);
  const fim = new Date(apontamento.data_fim);
  const ehFuturo = ini > new Date();

  return (
    <div style={{
      border: `1px solid ${COR_L.borda}`, borderRadius: '5px',
      padding: '8px 12px', fontSize: '12px',
      display: 'flex', alignItems: 'center', gap: '10px',
      backgroundColor: COR_L.branco,
      fontFamily: "'Manrope', sans-serif",
    }}>
      {/* Badge de horas */}
      <span style={{
        backgroundColor: COR_L.azul, color: COR_L.branco,
        padding: '4px 10px', borderRadius: '4px',
        fontSize: '12px', fontWeight: '700',
        minWidth: '60px', textAlign: 'center',
      }}>
        {Number(apontamento.horas).toFixed(2)}h
      </span>

      {/* Datas */}
      <div style={{ flex: 1 }}>
        <div style={{ color: COR_L.texto, fontSize: '12px', fontWeight: '500' }}>
          {ini.toLocaleDateString('pt-BR')}
          {' '}
          <span style={{ color: COR_L.muted }}>
            {ini.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            {' – '}
            {fim.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          {ehFuturo && (
            <span style={{
              marginLeft: '6px', fontSize: '9px', fontWeight: '700',
              padding: '1px 6px', borderRadius: '8px',
              backgroundColor: COR_L.verde + '20', color: COR_L.verde,
              border: `1px solid ${COR_L.verde}55`,
              letterSpacing: '0.5px',
            }}>FUTURO</span>
          )}
        </div>
        {apontamento.descricao && (
          <div style={{ color: COR_L.muted, fontSize: '11px', marginTop: '2px' }}>
            {apontamento.descricao}
          </div>
        )}
      </div>

      {/* Usuário que apontou */}
      {apontamento.usuario_nome && (
        <span style={{ fontSize: '11px', color: COR_L.muted, whiteSpace: 'nowrap' }}>
          👤 {apontamento.usuario_nome}
        </span>
      )}

      {/* Botões */}
      {podeEditar && (
        <>
          <button
            onClick={onEditar} title="Editar apontamento"
            style={{
              border: `1px solid ${COR_L.borda}`, borderRadius: '4px',
              backgroundColor: COR_L.branco, color: COR_L.azul,
              cursor: 'pointer', padding: '4px 8px',
              fontSize: '11px', fontWeight: '600',
              fontFamily: "'Manrope', sans-serif",
            }}
          >✎</button>
          <button
            onClick={onExcluir} title="Excluir apontamento (e bloco do calendário)"
            style={{
              border: `1px solid ${COR_L.borda}`, borderRadius: '4px',
              backgroundColor: COR_L.branco, color: COR_L.vermelho,
              cursor: 'pointer', padding: '4px 8px',
              fontSize: '11px', fontWeight: '600',
              fontFamily: "'Manrope', sans-serif",
            }}
          >🗑</button>
        </>
      )}
    </div>
  );
}

export default TarefaModal;
